import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator, TouchableOpacity, Platform, Image } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, FormHeader, Button, colors, spacing, typography } from '../components';
import { Input, CountryPicker, IndustryPicker, PhoneInput } from '../components/design-system';
import { parseInternationalPhoneNumber } from '../utils/phoneNumber';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { MapLocationPicker } from '../components/MapLocationPicker';
import { BusinessHoursForm, BusinessHoursValue, businessHoursFrom, validateBusinessHours } from '../components/business/BusinessHoursForm';
import { useAppContext } from '../contexts/AppContext';
import { useAppStore } from '../store/appStore';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { apiService } from '../services/api';
import { handleApiError } from '../utils/errorHandler';
import { useLocale } from '../contexts/LocaleContext';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { designTokens } from '../theme/design-system';

type PickedLocation = { latitude: number; longitude: number; address: string; city?: string; country?: string };

/**
 * Settings → Business profile (owner/admin): who the business is (logo,
 * name, email, phone, industry), where it is (address and map pin) and how
 * it works (timezone, currency, working days and hours — the same form as
 * the creation wizard).
 */
export const BusinessProfileScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, activeTab, setActiveTab } = useAppContext();
  const { currentBusiness, setCurrentBusiness } = useAppStore();
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [address, setAddress] = useState({ address: '', city: '', country: '' });
  const [location, setLocation] = useState<PickedLocation | null>(null);
  const [showMap, setShowMap] = useState(false);
  const [hours, setHours] = useState<BusinessHoursValue | null>(null);
  const [industry, setIndustry] = useState('');
  const [details, setDetails] = useState({ name: '', email: '' });
  // Printed on invoices and other documents; the VAT number also feeds the Saudi e-invoice QR.
  const [legal, setLegal] = useState({ legalName: '', vatNumber: '', crNumber: '' });
  const [logo, setLogo] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  // PhoneInput shows its own dial code: it gets the national number + country.
  const [phone, setPhone] = useState({ national: '', country: undefined as string | undefined });
  const phoneRef = useRef<any>(null);

  const load = useCallback(async () => {
    if (!currentBusiness?._id) return;
    setLoading(true);
    try {
      const res = await apiService.getBusinessById(currentBusiness._id);
      const b: any = res.statusCode === 1000 && res.data ? res.data : currentBusiness;
      setAddress({ address: b.address || '', city: b.city || '', country: b.country || '' });
      setIndustry(b.industry || '');
      setDetails({ name: b.name || '', email: b.email || '' });
      setLegal({ legalName: b.legalName || '', vatNumber: b.vatNumber || '', crNumber: b.crNumber || '' });
      setLogo(b.logo || null);
      let national = '';
      let country: string | undefined;
      if (b.phone) {
        try {
          const parsed = parseInternationalPhoneNumber(String(b.phone).startsWith('+') ? b.phone : `+${b.phone}`);
          if (parsed?.nationalNumber) { national = parsed.nationalNumber; country = parsed.country || undefined; }
        } catch { /* leave empty */ }
      }
      setPhone({ national, country });
      setLocation(b.location?.latitude != null
        ? { latitude: b.location.latitude, longitude: b.location.longitude, address: b.address || '' }
        : null);
      setHours(businessHoursFrom(b));
    } catch (err) {
      setHours(businessHoursFrom(currentBusiness));
      const { title, message, type } = handleApiError(err);
      showNotification(type, title, message);
    } finally {
      setLoading(false);
    }
  }, [currentBusiness?._id]);

  useEffect(() => { load(); }, [load]);

  const onPickLogo = async () => {
    if (!currentBusiness?._id) return;
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      showNotification('warning', t('business.photoPermissionTitle'), t('business.photoPermissionBody'));
      return;
    }
    const picked = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ImagePicker.MediaTypeOptions.Images, allowsEditing: true, aspect: [1, 1], quality: 0.8 });
    if (picked.canceled || !picked.assets[0]) return;
    setUploadingLogo(true);
    try {
      const res = await apiService.uploadBusinessLogo(currentBusiness._id, picked.assets[0].uri, `logo-${currentBusiness._id}.jpg`, 'image/jpeg');
      const url = res.data?.logo;
      if (res.statusCode === 1000 && url) {
        setLogo(url);
        setCurrentBusiness({ ...currentBusiness, logo: url } as any);
      } else {
        showNotification('error', t('businessProfile.saveFailed'), res.errors?.[0]?.message || '');
      }
    } catch (err) {
      const { title, message, type } = handleApiError(err);
      showNotification(type, title, message);
    } finally {
      setUploadingLogo(false);
    }
  };

  const onSave = async () => {
    if (!hours) return;
    const problem = validateBusinessHours(hours);
    if (problem) {
      showNotification('error', t('businessProfile.title'), t(problem as any));
      return;
    }
    if (details.name.trim().length < 2 || !/^\S+@\S+\.\S+$/.test(details.email.trim())) {
      showNotification('error', t('businessProfile.title'), t('businessProfile.detailsInvalid'));
      return;
    }
    if (industry === 'other') {
      showNotification('error', t('businessProfile.title'), t('industry.otherPlaceholder'));
      return;
    }
    setSaving(true);
    try {
      const phoneValue = phoneRef.current?.getValue?.();
      const res = await apiService.updateBusinessSettings({
        name: details.name.trim(),
        email: details.email.trim().toLowerCase(),
        phone: phoneValue?.nationalNumber ? (phoneValue.e164Format || phoneValue.formattedNumber) : '',
        ...(industry.trim() ? { industry: industry.trim() } : {}),
        address: address.address.trim(),
        city: address.city.trim(),
        country: address.country.trim(),
        location: location ? { latitude: location.latitude, longitude: location.longitude } : null,
        timezone: hours.timezone,
        currency: hours.currency,
        workingHours: hours.workingHours,
        legalName: legal.legalName.trim(),
        vatNumber: legal.vatNumber.trim(),
        crNumber: legal.crNumber.trim(),
      });
      if (res.statusCode === 1000 && res.data) {
        // Keep the session's copy current (orders/vendors read its currency).
        if (currentBusiness) setCurrentBusiness({ ...currentBusiness, ...(res.data as any) });
        showNotification('success', t('businessProfile.saved'), '');
      } else {
        showNotification('error', t('businessProfile.saveFailed'), res.errors?.[0]?.message || '');
      }
    } catch (err) {
      const { title, message, type } = handleApiError(err);
      showNotification(type, title, message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications
          showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={<FormHeader title={t('businessProfile.title')} onBack={() => navigateTo('settings')} />}
    >
      {loading || !hours ? (
        <View style={styles.center}><ActivityIndicator size="large" color={colors.primary as string} /></View>
      ) : (
        <ScrollView style={styles.scroll} contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
          <View style={styles.card}>
            <Text style={[styles.cardTitle, { textAlign: rtl.textStart }]}>{t('businessProfile.details')}</Text>
            <TouchableOpacity style={[styles.logoRow, { flexDirection: rtl.rowDirection }]} onPress={onPickLogo} disabled={uploadingLogo} activeOpacity={0.85}>
              {logo ? <Image source={{ uri: logo }} style={styles.logo} /> : (
                <View style={[styles.logo, styles.logoPlaceholder]}><Ionicons name="business" size={26} color={colors.primary as string} /></View>
              )}
              {uploadingLogo ? <ActivityIndicator color={colors.primary as string} /> : (
                <Text style={styles.logoAction}>{t(logo ? 'businessProfile.changeLogo' : 'business.addLogo')}</Text>
              )}
            </TouchableOpacity>
            <Input label={t('business.name')} value={details.name} onChangeText={(v) => setDetails((d) => ({ ...d, name: v }))} variant="outlined" />
            <Input label={t('business.email')} value={details.email} onChangeText={(v) => setDetails((d) => ({ ...d, email: v }))} keyboardType="email-address" autoCapitalize="none" variant="outlined" latin />
            <PhoneInput
              ref={phoneRef}
              label={t('business.phoneOptional')}
              value={phone.national}
              onChangeText={(v: string) => setPhone((p) => ({ ...p, national: v }))}
              initialCountry={phone.country}
              variant="outlined"
            />
            <IndustryPicker label={t('business.industry')} value={industry} onChange={setIndustry} />
          </View>

          <View style={styles.card}>
            <Text style={[styles.cardTitle, { textAlign: rtl.textStart }]}>{t('businessProfile.legal')}</Text>
            <Text style={[styles.legalHint, { textAlign: rtl.textStart }]}>{t('businessProfile.legalHint')}</Text>
            <Input label={t('businessProfile.legalName')} value={legal.legalName} onChangeText={(v) => setLegal((l) => ({ ...l, legalName: v }))} variant="outlined" />
            <Input label={t('businessProfile.vatNumber')} value={legal.vatNumber} onChangeText={(v) => setLegal((l) => ({ ...l, vatNumber: v }))} autoCapitalize="characters" variant="outlined" latin />
            <Input label={t('businessProfile.crNumber')} value={legal.crNumber} onChangeText={(v) => setLegal((l) => ({ ...l, crNumber: v }))} autoCapitalize="characters" variant="outlined" latin />
          </View>

          <View style={styles.card}>
            <Text style={[styles.cardTitle, { textAlign: rtl.textStart }]}>{t('businessProfile.location')}</Text>
            <TouchableOpacity style={[styles.mapButton, { flexDirection: rtl.rowDirection }]} onPress={() => setShowMap(true)} activeOpacity={0.85}>
              <Ionicons name="location" size={20} color={colors.primary as string} />
              <Text style={[styles.mapButtonText, { textAlign: rtl.textStart }]} numberOfLines={2}>
                {location ? (location.address || address.address || t('businessProfile.pinSet')) : t('business.searchLocation')}
              </Text>
              <DirectionalIcon name="chevron-forward" size={18} color={colors.textLight} />
            </TouchableOpacity>
            <Input label={t('businessProfile.address')} value={address.address} onChangeText={(v) => setAddress((a) => ({ ...a, address: v }))} variant="outlined" />
            <Input label={t('business.city')} value={address.city} onChangeText={(v) => setAddress((a) => ({ ...a, city: v }))} variant="outlined" />
            <CountryPicker label={t('business.country')} value={address.country} onChange={(v) => setAddress((a) => ({ ...a, country: v }))} />
          </View>

          <View style={styles.card}>
            <Text style={[styles.cardTitle, { textAlign: rtl.textStart }]}>{t('businessHours.title')}</Text>
            <BusinessHoursForm value={hours} onChange={setHours} />
          </View>

          <Button title={t('common.save')} onPress={onSave} loading={saving} disabled={saving} variant="primary" size="lg" fullWidth />
        </ScrollView>
      )}

      <MapLocationPicker
        visible={showMap}
        onClose={() => setShowMap(false)}
        onLocationSelect={(loc: any) => {
          setLocation(loc);
          setAddress((a) => ({
            address: loc.address || a.address,
            city: loc.city || a.city,
            country: loc.country || a.country,
          }));
          setShowMap(false);
        }}
        initialLocation={location as any}
      />
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: spacing.xl },
  scroll: { flex: 1 },
  content: { padding: spacing.md, gap: spacing.md, maxWidth: 720, width: '100%', alignSelf: 'center', paddingBottom: spacing.xl * 2 },
  card: {
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: designTokens.borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surfaceRaised,
  },
  cardTitle: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.textDark },
  legalHint: { fontSize: typography.fontSize.xs, color: colors.textLight, marginTop: -4 },
  mapButton: {
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  logoRow: { alignItems: 'center', gap: spacing.md },
  logo: { width: 64, height: 64, borderRadius: 32 },
  logoPlaceholder: { alignItems: 'center', justifyContent: 'center', backgroundColor: colors.background.secondary },
  logoAction: { fontSize: typography.fontSize.sm, color: colors.primary, fontFamily: typography.fontFamily.semibold },
  mapButtonText: { flex: 1, fontSize: typography.fontSize.sm, color: colors.textDark },
});
